import { setContext } from 'apollo-link-context'
import { ApolloLink, Observable } from 'apollo-link'
import { onError } from 'apollo-link-error'
import { RetryLink } from 'apollo-link-retry'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ErrorCode } from '@constants'
import { isSuccess } from '@/utils/result-pattern'

const getHttpEndpoint = (req, $config) => {
  const baseURL = process.client
    ? `${window.location.protocol}//${window.location.host}`
    : req
    ? `${req.headers['x-forwarded-proto'] || 'http'}://${req.headers.host}`
    : $config.baseUrl

  return `${baseURL}/api/graphql`
}

const exchange = async (app, store, redirect, operation, forward, observer) => {
  try {
    const result = await app.$auth.refresh()

    if (isSuccess(result)) {
      // retry the original request with refreshed token
      forward(operation).subscribe({
        next: observer.next.bind(observer),
        error: observer.error.bind(observer),
        complete: observer.complete.bind(observer)
      })
    } else {
      // refresh failed, sign out user
      await app.$auth.signout()
      redirect('/')
      observer.error(new Error('authentication_failed'))
    }
  } catch (error) {
    // refresh threw error, sign out user
    await app.$auth.signout()
    redirect('/')
    observer.error(error)
  }
}

const headers = (req, store) => {
  return setContext(() => {
    const headers = {}
    const locale = store.get('language/locale') || 'en'

    if (req) {
      const ip = req.headers['x-forwarded-for'] || req.socket.remoteAddress

      if (ip) {
        headers['x-forwarded-for'] = ip
      }

      if (req.headers['user-agent']) {
        headers['user-agent'] = req.headers['user-agent']
      }

      headers['x-forwarded-proto'] = req.headers['x-forwarded-proto'] || (req.connection?.encrypted ? 'https' : 'http')
    }

    // add visitor id for anonymous users
    const user = store.get('user/user')
    if (!user && typeof localStorage !== 'undefined') {
      const visitorId = localStorage.getItem('VisitorId')
      if (visitorId) {
        headers['X-VISITOR-ID'] = visitorId
      }
    }

    return {
      headers: {
        ...headers,
        'X-Request-Language': locale
      }
    }
  })
}

const responseHeaders = (networkError) => {
  const headers = networkError.response?.headers
  return headers ? Object.fromEntries(headers.entries()) : undefined
}

const isAuthError = (graphQLErrors) => {
  return graphQLErrors?.some(
    (error) =>
      error.extensions?.code === ErrorCode.UNAUTHORISED || error.extensions?.code === ErrorCode.AUTH_NOT_AUTHENTICATED
  )
}

const errors = (app, store, redirect, error) => {
  return onError(({ operation, graphQLErrors, networkError, forward }) => {
    if (isAuthError(graphQLErrors)) {
      const user = store.get('user/user')
      if (!user) {
        return // skip refresh for anonymous users
      }

      return new Observable((observer) => exchange(app, store, redirect, operation, forward, observer))
    }

    if (graphQLErrors?.length) {
      console.error('a graphql error was raised', {
        operationName: operation.operationName,
        variables: JSON.stringify(operation.variables),
        errors: graphQLErrors.map(({ message, locations, path, extensions }) => ({
          message,
          path,
          locations,
          extensions
        }))
      })
    }

    if (networkError) {
      console.error('network error', {
        operationName: operation.operationName,
        variables: JSON.stringify(operation.variables),
        name: networkError.name,
        statusCode: networkError.statusCode,
        networkError: networkError.message,
        bodyText: networkError.bodyText,
        responseHeaders: responseHeaders(networkError)
      })
      error({ statusCode: networkError.statusCode || 500, message: networkError.message })
    }
  })
}

const retry = () => {
  return new RetryLink({
    delay: {
      initial: 100,
      max: 2000,
      jitter: true
    },
    attempts: {
      max: 3,
      retryIf: (error) => {
        // A 502 connection failure can be transient. Do not amplify explicit overload or a 504
        // from a backend that already spent its full request timeout trying to respond.
        const doNotRetryCodes = [500, 400, 401, 403, 429, 503, 504]
        return !!error && !doNotRetryCodes.includes(error.statusCode)
      }
    }
  })
}

const emitter = () => {
  return new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      if (typeof window === 'undefined') return response
      const context = operation.getContext()
      const headers = context.response?.headers

      if (headers) {
        // emit events for headers starting with x-emit-
        for (const [name, value] of headers.entries()) {
          if (name.toLowerCase().startsWith('x-emit-')) {
            const eventName = name.substring(7) // remove 'x-emit-' prefix
            window.$nuxt?.$emit(eventName, value)
          }
        }
      }

      return response
    })
  })
}

export default ({ app, store, redirect, error, $config, req }) => {
  const link = ApolloLink.from([errors(app, store, redirect, error), headers(req, store), retry(), emitter()])

  return {
    link,
    httpEndpoint: getHttpEndpoint(req, $config),
    cache: new InMemoryCache()
  }
}
